import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Content } from "@iventis/translations/content/typed-content";
import { Header5 } from "@iventis/styles/src/components/texts";
import { TreeAction, StyledTreeActionsContainer, TreeBrowserHeaderComponent } from "@iventis/tree-browser/components/tree-browser-header";
import { useMachine } from "@xstate/react";
import React, { useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TreeBrowserComponent } from "@iventis/tree-browser";
import { LinearProgress } from "@mui/material";
import { Authorisation } from "@iventis/domain-model/model/authorisation";
import { BehaviorSubject } from "rxjs";
import { User } from "@iventis/domain-model/model/user";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { useFunctionality, useRefEffectCallback } from "@iventis/utilities";
import {
    createUserNodeComponent,
    addNewPeopleMachineId,
    PeopleViewAction,
    peopleViewMachine as _peopleViewMachine,
    PeopleViewMachineEventType,
    isAllUsersSelected,
    createPeopleNodeTree,
    AdminTreeNodeType,
    AdminPersonNode,
    PeopleNodeTree,
    AddNewPeopleComponent,
} from "@iventis/people";
import { toast } from "@iventis/toasts/src/toast";
import { useProject } from "@iventis/permissions/src/use-project";
import { ResetPasswordIcon } from "@iventis/components/src/reset-password-icon";
import { formPadding, styled } from "@iventis/styles";
import { SortMethod, UserLimitReachedMessage, LimitChipRemaining, UserLimitRemainingMessage, CustomDialogAnchor, CustomDialog } from "@iventis/components";
import { ExtractRefFromComponent } from "@iventis/types/useful.types";
import { LimitedProjectResource } from "@iventis/domain-model/model/limitedProjectResource";
import { useProjectLimiter } from "@iventis/permissions/src/project-limiter-hook";
import { authSlice, AuthState } from "@iventis/permissions/src/auth.slice";
import { getPeople, getTeams, postNewUserAndGroups } from "@iventis/permissions/src/people-api-functions";
import { NoAccessOverlay } from "@iventis/permissions/src/upgrade-limit-overlay";
import { SsoConfig } from "@iventis/domain-model/model/ssoConfig";
import { permissionsApi } from "@iventis/api/src/api";
import { useQuery } from "@tanstack/react-query";
import PeopleViewConfirmation from "./people-view-confirmation";
import { PeoplePageHeader, PeoplePageTopContent } from "./teams-view";

type AddNewPeopleRef = ExtractRefFromComponent<typeof AddNewPeopleComponent>;

export const PeopleView: React.FC = () => {
    const translate = useIventisTranslate();

    const dispatch = useDispatch();

    const addNewPeopleRef = useRef<AddNewPeopleRef>(null);

    const { projectId, projectAdmin, globalAuthorisation, userId } = useSelector((state: { auth: AuthState }) => state.auth.user);
    const functionality = useFunctionality();

    const [canUse, { projectUsage, maximumUsers }] = useProjectLimiter();

    const project = useProject();
    const { current: peopleViewMachine } = useRef(
        _peopleViewMachine({
            toast,
            getPeople,
            getTeams,
            postNewUserAndGroups: postNewUserAndGroups((data) => dispatch(authSlice.actions.setUsage({ usage: "adjustUsers", adjustment: data.length }))),
            translate,
        })
    );
    const [state, send] = useMachine(peopleViewMachine, { context: { projectId } });
    const isUserAdmin = useMemo(() => globalAuthorisation === Authorisation.Admin || projectAdmin, []);
    const { people, selectedPeople } = state.context;

    const currentUserCount = projectUsage?.users;

    const [action, setAction] = useState<PeopleViewAction>(null);

    const nodeTree: PeopleNodeTree = useMemo(() => (state.context.people ? createPeopleNodeTree(state.context.people) : undefined), [state.context.people]);

    const { data: ssoConfig } = useQuery<SsoConfig>(["sso-config", projectId], () => permissionsApi.get<SsoConfig>(`projects/${projectId}/sso-config`).then((res) => res.data), {
        enabled: functionality?.sso && isUserAdmin,
    });
    // Subject needed to be passed into the createUserNodeComponent
    const { current: selectedPeopleSubject } = useRefEffectCallback(
        new BehaviorSubject<User[]>([]),
        (subject) => {
            subject.next(people?.filter((p) => selectedPeople.includes(p.id)));
            return subject;
        },
        [selectedPeople]
    );

    const peopleNodeComponents = useMemo(
        () => ({
            [AdminTreeNodeType.Root]: undefined,
            [AdminTreeNodeType.User]: createUserNodeComponent({
                enableCheckBox: () => true,
                showCheckBoxColumn: true,
                showOverflowMenuColumn: false,
                showSsoColumn: ssoConfig?.enabled,
                onCheckChange: (node: AdminPersonNode, value: boolean) => {
                    send({ type: PeopleViewMachineEventType.UPDATE_SELECTION, payload: { ids: [node.id], selected: value } });
                },
                checkValuesSubject: selectedPeopleSubject,
            }),
        }),
        [ssoConfig]
    );

    const alphabeticalSortMethod = useMemo(() => translate(Content.people.surnameAlphabetical), [translate]);

    const sortMethods: SortMethod[] = [{ displayName: alphabeticalSortMethod, id: `nameAsc` }];

    const isAllPeopleSelected = useMemo(() => isAllUsersSelected(people, selectedPeople), [people, selectedPeople]);

    return (
        <>
            <StyledContainer>
                <PeoplePageTopContent>
                    <PeoplePageHeader>
                        <Header5>
                            <FontAwesomeIcon icon={{ prefix: "far", iconName: "users" }} /> {translate(Content.people.people)}
                        </Header5>
                    </PeoplePageHeader>
                    {/* We do not display this banner if the maximum users is one, we display the overlay instead */}
                    {maximumUsers !== 1 && (
                        <>
                            {!canUse("users") && <UserLimitReachedMessage />}
                            {maximumUsers - currentUserCount === 1 && <UserLimitRemainingMessage />}
                        </>
                    )}
                </PeoplePageTopContent>
                {state.matches("peopleLoading") && <LinearProgress />}
                {!(state.matches("peopleLoading") || state.matches("peopleLoadingError")) && (
                    <>
                        <TreeBrowserHeaderComponent
                            selectAllValue={isAllPeopleSelected}
                            selectAll={() => send({ type: PeopleViewMachineEventType.TOGGLE_SELECT_ALL })}
                            sortMethods={sortMethods}
                            selectedSortMethod={sortMethods[0]}
                            onSortMethodChange={() => undefined}
                            hideSelectAll={false}
                        >
                            <StyledTreeActionsContainer>
                                <LimitChipRemaining currentUsage={currentUserCount} maxUsage={maximumUsers} resource={LimitedProjectResource.User} />
                                <TreeAction
                                    text={translate(Content.people.add_people.add_user)}
                                    disabled={!isUserAdmin || currentUserCount === maximumUsers}
                                    onClick={() => send({ type: PeopleViewMachineEventType.ADDING_NEW_PEOPLE })}
                                    dataTestId="add-user"
                                >
                                    <FontAwesomeIcon icon={{ prefix: "far", iconName: "user-plus" }} size="xs" />
                                </TreeAction>
                                <TreeAction
                                    text={translate(Content.people.remove_people.remove_user)}
                                    disabled={!isUserAdmin || selectedPeople.length < 1 || selectedPeople.includes(userId)}
                                    onClick={() => setAction(PeopleViewAction.REMOVE)}
                                    dataTestId="remove-user"
                                >
                                    <FontAwesomeIcon icon={{ prefix: "far", iconName: "user-slash" }} size="xs" />
                                </TreeAction>
                                <TreeAction
                                    text={translate(Content.people.reset_password.reset_password_title)}
                                    disabled={
                                        !isUserAdmin ||
                                        selectedPeople.length < 1 ||
                                        selectedPeople.includes(userId) ||
                                        people?.filter((p) => selectedPeople.includes(p.id)).some((p) => p.ssoEnabled)
                                    }
                                    onClick={() => setAction(PeopleViewAction.RESET_PASSWORD)}
                                >
                                    <ResetPasswordIcon size="sm" />
                                </TreeAction>
                                {ssoConfig?.enabled && (
                                    <TreeAction
                                        text={translate(Content.people.sso.title)}
                                        disabled={!isUserAdmin || selectedPeople.length < 1 || people?.filter((p) => selectedPeople.includes(p.id)).some((p) => p.ssoEnabled)}
                                        onClick={() => setAction(PeopleViewAction.ENABLE_SSO)}
                                        dataTestId="enable-sso"
                                    >
                                        <FontAwesomeIcon icon={{ prefix: "far", iconName: "key" }} size="xs" />
                                    </TreeAction>
                                )}
                                {ssoConfig?.enabled && (
                                    <TreeAction
                                        text={translate(Content.people.sso_disable.title)}
                                        disabled={!isUserAdmin || selectedPeople.length < 1 || people?.filter((p) => selectedPeople.includes(p.id)).some((p) => !p.ssoEnabled)}
                                        onClick={() => setAction(PeopleViewAction.DISABLE_SSO)}
                                        dataTestId="disable-sso"
                                    >
                                        <span className="fa-stack fa-xs">
                                            <FontAwesomeIcon icon={{ prefix: "far", iconName: "key" }} className="fa-stack-1x" />
                                            <FontAwesomeIcon icon={{ prefix: "fas", iconName: "slash" }} className="fa-stack-1x" />
                                        </span>
                                    </TreeAction>
                                )}
                            </StyledTreeActionsContainer>
                        </TreeBrowserHeaderComponent>
                        <StyledTreeBrowserContent>
                            <StyledTreeBrowserComponent
                                className="tree-browser"
                                node={nodeTree}
                                displayRootNode={false}
                                components={peopleNodeComponents}
                                openNodes={nodeTree.childNodes.map(({ id }) => id)}
                            />
                        </StyledTreeBrowserContent>
                        <StyledDialog
                            anchor={CustomDialogAnchor.TOP}
                            open={state.matches("addingNewPeople")}
                            maxWidth="lg"
                            onClose={() => addNewPeopleRef.current.cancel()}
                            className="add-people-modal"
                        >
                            {state.children[addNewPeopleMachineId] != null && (
                                <AddNewPeopleComponent
                                    ref={addNewPeopleRef}
                                    addNewPeopleActor={state.children[addNewPeopleMachineId]}
                                    project={project}
                                    translate={translate}
                                    specifyUserSso={ssoConfig?.enabled}
                                />
                            )}
                        </StyledDialog>
                        {action != null && <PeopleViewConfirmation action={action} setAction={setAction} projectId={projectId} selectUserIds={selectedPeople} send={send} />}
                        {projectUsage.users === 1 && maximumUsers === 1 && <NoAccessOverlay upgradeType="maximumUsers" />}
                    </>
                )}
            </StyledContainer>
        </>
    );
};

const StyledTreeBrowserComponent = styled(TreeBrowserComponent)``;

const StyledTreeBrowserContent = styled.div`
    flex-shrink: 1;
    overflow-x: hidden;
`;

const StyledContainer = styled.div`
    flex-grow: 1;
    height: 100%;
    min-width: 0;
    overflow: hidden;

    display: flex;
    flex-direction: column;
    position: relative;
`;

const StyledDialog = styled(CustomDialog)`
    .form-wizard-content {
        padding-top: ${formPadding};
        padding-bottom: ${formPadding};
    }
`;
